import { http } from '../lib'
import { get, has } from 'lodash'
import uuid from 'uuid/v4'
import {
    announceObjectsLoaded,
    getObjectsFromState,
    announceSavingObject,
    announceNewObjectSaved,
    announceObjectSaveFailed,
    getObject,
    announceDeletingObject,
    announceObjectDeleted,
    announceObjectDeleteFailed,
    announceObjectSaved,
    getIsSavingObject
} from './orm_objects_actions'

import { SubmissionError } from 'redux-form'
import { ORM_LIST_KEY } from '.'

import {
    getMissingItemIds,
    announceMatchingItemsLoading,
    announceMatchingItemsLoaded,
    announceMatchingItemsLoadFailed,
    announceListLoadFailed,
    announceListLoaded,
    shouldFetchList,
    announceListLoading,
    announceHandlingPost,
    announcePostCompleted
} from './orm_list_actions'

import { getSubmissionErrors } from '../actions/Error'

export function tryFetchMatchingItems(listKey, entityKey, requiredItemIds, url, fixResponse, on_done) {
    // The second half of tryFetchListAndItems, separated out for clarity
    return async (dispatch, getState) => {
        
        if (!requiredItemIds) {
            return []
        }
        const state = getState()
        let requestFilter = {}
        const unmatching_item_ids = getMissingItemIds(state, requiredItemIds, entityKey)
        if (listKey) {
            const l = state[ORM_LIST_KEY][listKey]
            if (l && l.loadingMatchingItems && unmatching_item_ids.length === 0) {
                return getObjectsFromState(state, requiredItemIds)
            }
            requestFilter = Object.assign({}, requestFilter, l && l.filter, {page:1})
        }
        
        if (unmatching_item_ids.length > 0) {
            dispatch(announceMatchingItemsLoading(entityKey, listKey, requiredItemIds))
            requestFilter.ids = unmatching_item_ids
            return http
                .get(url || entityKey, requestFilter)
                .then(([json, response, success]) => {
                    if (success) {
                        json = fixResponse ? fixResponse(json) : json
                        dispatch(announceMatchingItemsLoaded(entityKey, listKey))
                        dispatch(announceObjectsLoaded(entityKey, json))

                        if ( on_done ) {
                            on_done(json)
                        }
                        
                    } else {

                        dispatch(announceListLoadFailed(
                            entityKey, listKey,
                            "Failed to load list: " + entityKey + ": " + listKey + " : " + json.toString()))
                    }
                })
                .catch(function (error) {
                    dispatch(announceMatchingItemsLoadFailed(entityKey, listKey, "Failed to load entity list: " + error))
                })
        } else {
            return getObjectsFromState(state, requiredItemIds)
        }
    }
}

export function tryFetchListAndItems(listKey, entityKey, url, fixResponse, forceUpdate) {
    return async (dispatch, getState) => {
        const state = getState()

        const l = get(state, `${ORM_LIST_KEY}.${listKey}`) || {}

        let fetchPromise
        const visibleItemIds = l.visibleItemIds

        if (!forceUpdate && !shouldFetchList(state, listKey)) {
            if (visibleItemIds) {
                return dispatch(tryFetchMatchingItems(listKey, visibleItemIds, entityKey, url, fixResponse))
            } else {
                return Promise.resolve([])
            }
        } else {
            dispatch(announceListLoading(entityKey, listKey))
            const params = Object.assign({}, l.filter, l.pagination)
            if ( url === undefined ) {
                url = entityKey + "/"
            }
            fetchPromise = http.get(url, params, {})
        }

        let json, response, success, pagination
        try {
            [json, response, success, pagination] = await fetchPromise
        } catch (error) {
            dispatch(announceListLoadFailed(
                entityKey, listKey,
                "Failed to load list: " + entityKey + ": " + listKey + " : " + error))
            throw error
        }
        if (!success) {
            dispatch(announceListLoadFailed(entityKey, listKey, json.errors))
        } else {
            dispatch(announceObjectsLoaded(entityKey, json))
            dispatch(announceListLoaded(entityKey, listKey, json, pagination))
        }
        return json
    }
}

export function fetchListIfNeeded(listKey, entityKey, url, fixResponse, forceUpdate) {
    return (dispatch, getState) => {
        const state = getState()
        if (forceUpdate || shouldFetchList(state, listKey)) {
            if (entityKey === undefined) {
                entityKey = state[ORM_LIST_KEY][listKey].entityKey
            }
            return dispatch(tryFetchListAndItems(listKey, entityKey, url, fixResponse, forceUpdate))
        }
        return Promise.resolve([])
    }
}

export function ensureObjectsLoaded(entityKey, listKey, objectIds, url, fixResponse, on_done) {
    return (dispatch, getState) => {
        const objectIdsToLoad = getMissingItemIds(getState(), objectIds, entityKey)
        return dispatch(tryFetchMatchingItems(listKey, entityKey, objectIdsToLoad, url, fixResponse, on_done))
    }
}

export function ensureObjectLoaded(entityKey, listKey, objectId, url, fixResponse, on_done) {
    return ensureObjectsLoaded(entityKey, listKey, [objectId], fixResponse, on_done)
}

export function saveNewObject(entityKey, listKey, item, onDone) {
    const newValues = Object.assign({}, item)
    delete newValues.isNewInstance
    delete newValues.id

    const unique_id = uuid()
    
    return (dispatch, getState) => {
        const state = getState()
        dispatch(announceSavingObject(entityKey, listKey, {id:unique_id}))

        return http
            .post(state, entityKey, newValues)
            .then(([json, response, success]) => {
                if (success) {
                    dispatch(announceNewObjectSaved(entityKey, listKey, json, unique_id))
                    if ( onDone ) {
                        onDone(json)
                    }
                    return json
                } else {
                    const submission_errors = getSubmissionErrors(json)
                    dispatch(announceObjectSaveFailed(entityKey,
                                                      listKey,
                                                      Object.assign({id:unique_id}, json),
                                                      submission_errors))
                    return { 'errors': submission_errors }
                }
            })
            .catch(json => {
                dispatch(announceObjectSaveFailed(entityKey, listKey, {id:unique_id}, json.errors))
                if (json.errors) {
                    return { 'errors': json.errors }
                } else {
                    return { 'errors': {_error: json.message } }
                }
            })
    }
}

export function patchObject(entityKey, listKey, item, onDone) {
    const newValues = Object.assign({}, item)

    return (dispatch, getState) => {
        const state = getState()
        dispatch(announceSavingObject(entityKey, listKey, item))
        const url = `${entityKey}/${item.id}`
        return http
            .patch(state, url, newValues)
            .then(([json, response]) => {
                if (response.status === 400) {
                    const submission_errors = getSubmissionErrors(json)
                    dispatch(announceObjectSaveFailed(entityKey,
                                                      listKey,
                                                      Object.assign({id:item.id}, json),
                                                      submission_errors))
                    return { 'errors': submission_errors }
                }
                dispatch(announceObjectSaved(entityKey, listKey, json))

                if ( onDone ) {
                    onDone(json)
                }
                return json
            }).catch(err => {
                dispatch(announceObjectSaveFailed(entityKey,
                                                  listKey,
                                                  Object.assign({id:item.id, error:err.errors}),
                                                  err.errors))
                return { 'errors': err.errors }
            })
    }
}

export function saveObject(entityKey, listKey, item, onDone) {
    return (dispatch, getState) => {
        const state = getState()
        dispatch(announceSavingObject(entityKey, listKey, item))
        const url = `${entityKey}/${item.id}`
        return http
            .put(state, url, item)
            .then(([json, response]) => {
                if (response.status === 400) {
                    const submission_errors = getSubmissionErrors(json)
                    dispatch(announceObjectSaveFailed(entityKey, listKey,
                                                      Object.assign({id:item.id}, json),
                                                      submission_errors))
                    return {'errors': submission_errors}
                }

                dispatch(announceObjectSaved(entityKey, listKey, json))

                if ( onDone ) {
                    onDone(json)
                }
                
                return json
            }).catch(err => {
                dispatch(announceObjectSaveFailed(entityKey,
                                                  listKey,
                                                  Object.assign({id:item.id, error:err.errors}),
                                                  err.errors))
                console.error(err.errors)
                return { 'errors': err.errors }
            })
    }
}

export function deleteObject(entityKey, listKey, itemId, onDone) {
    return (dispatch, getState) => {
        const item = getObject(getState(), entityKey, itemId)
        dispatch(announceDeletingObject(entityKey, listKey, itemId))
        if (item.isNewInstance) {
            if (onDone) {
                onDone()
            }
            return dispatch(announceObjectDeleted(entityKey, listKey, itemId))
        }
        return http
            .del(getState(),`${entityKey}/${itemId}`)
            .then(([json, response, success]) => {
                if (success) {
                    dispatch(announceObjectDeleted(entityKey, listKey, itemId))
                    if (onDone) {
                        onDone(json)
                    }
                    return json
                } else {
                    dispatch(announceObjectDeleteFailed(entityKey, listKey, itemId, json.errors))
                }
            })
    }
}

export function handlePost(entityKey, listKey, postData, onDone) {
    return (dispatch, getState) => {
        const state = getState()
        dispatch(announceHandlingPost(entityKey, listKey))

        return http
            .post(state, entityKey, postData)
            .then(([json, response]) => {
                dispatch(announcePostCompleted(entityKey, listKey))
                if ( onDone ) {
                    onDone(json)
                }
                return json
            }).catch(err => {
                if ( onDone ) {
                    onDone(err)
                }
                dispatch(announcePostCompleted(entityKey, listKey))
                return err
            })
    }
}
